import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck, ChevronLeft } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mfaPrompt, setMfaPrompt] = useState(null); // {mfa_token, user_email, full_name}
  const [code, setCode] = useState("");
  const { login, completeMfaLogin } = useAuth();
  const nav = useNavigate();

  const handlePostLogin = (user) => {
    if (user?.force_password_change) {
      nav("/settings/password-change", { replace: true });
      return;
    }
    if (user?.mfa_setup_required) {
      nav("/settings/mfa", { replace: true });
      return;
    }
    nav(landingFor(user.role), { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const result = await login(email, password, rememberMe);
      if (result?.mfa_required) {
        setMfaPrompt(result);
      } else {
        handlePostLogin(result.user);
      }
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = await completeMfaLogin(mfaPrompt.mfa_token, code);
      handlePostLogin(u);
    } catch (err) {
      setError(err.message || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F0FAFA]">
      <div className="hidden md:flex flex-col justify-between p-10 text-white relative overflow-hidden bg-[#0D7377]">
        <div className="absolute inset-0 opacity-30 mix-blend-multiply">
          <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&auto=format&fit=crop" alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <div className="h-12 w-12 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/logo.png" alt="Rabah Concept" className="h-11 w-11 object-contain" data-testid="login-brand-logo" />
            </div>
            <div>
              <p className="font-heading font-extrabold text-2xl" data-testid="login-brand-name">Rabah Concept</p>
              <p className="text-xs uppercase tracking-widest text-white/70">CQC compliant care platform</p>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold leading-tight">Person-centred care, evidenced for every CQC inspection.</h1>
          <p className="mt-4 text-white/80 text-base">Domiciliary care, supported living and day services — managed in one platform with built-in safeguarding, risk and compliance workflows.</p>
          <ul className="mt-6 space-y-1.5 text-sm text-white/80">
            <li>• GPS-verified electronic visit monitoring</li>
            <li>• Five Key Questions evidence dashboard</li>
            <li>• Safer recruitment, training matrix &amp; DBS tracking</li>
          </ul>
        </div>
        <p className="relative text-xs text-white/60">v1.0 · Health &amp; Social Care Act 2008 (Regulated Activities) Regulations 2014</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-2 mb-6">
            <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="Rabah Concept" className="h-9 w-9 object-contain" />
            </div>
            <p className="font-heading font-bold text-xl text-slate-900">Rabah Concept</p>
          </div>

          {!mfaPrompt && (
            <>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Sign in</h2>
              <p className="mt-1 text-sm text-slate-600">Use your work email and password to access your portal.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourcompany.co.uk" data-testid="login-email-input" className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</label>
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white" />
                </div>
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
                <label className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer" data-testid="login-remember-me-wrap">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    data-testid="login-remember-me"
                    className="h-4 w-4 rounded border-slate-300 text-[#0D7377] focus:ring-[#0D7377]/30"
                  />
                  Remember me on this device for 30 days
                </label>
                <div className="flex justify-end -mt-2">
                  <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm text-[#0D7377] font-medium hover:text-[#0A5C5F] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white font-semibold py-2.5 rounded-md transition-colors disabled:opacity-60 min-h-[44px]">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
                </button>
              </form>
              <p className="mt-6 text-xs text-slate-500">Need an account? Contact your Registered Manager or Super Admin.</p>
            </>
          )}

          {mfaPrompt && (
            <div className="animate-fade-in" data-testid="mfa-prompt">
              <button type="button" onClick={() => { setMfaPrompt(null); setCode(""); setError(""); }} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <div className="flex items-center gap-2 text-[#0D7377] mb-3">
                <ShieldCheck className="h-5 w-5" />
                <h2 className="font-heading text-2xl font-bold">Two-factor authentication</h2>
              </div>
              <p className="text-sm text-slate-600">Hello {mfaPrompt.full_name}. Enter the 6-digit code from your authenticator app, or one of your one-time recovery codes.</p>
              <form onSubmit={onSubmitMfa} className="mt-5 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Authentication code</label>
                  <input
                    autoFocus
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="123456 or recovery code"
                    inputMode="text"
                    autoComplete="one-time-code"
                    data-testid="mfa-code-input"
                    className="mt-1 w-full px-3 py-3 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white text-center font-mono tracking-widest text-lg"
                  />
                </div>
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
                <button type="submit" disabled={busy} data-testid="mfa-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white font-semibold py-2.5 rounded-md min-h-[44px]">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify &amp; sign in
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function landingFor(role) {
  if (role === "family_member") return "/family";
  if (role === "care_worker" || role === "bank_worker") return "/visits";
  return "/dashboard";
}
